/**
 * Cluster refresh tab (P3.3).
 *
 * Mounted as the Cluster Refresh tab on /ads-tools. Re-runs the keyword research pipeline
 * against the tenant's current company info, diffs against existing
 * ad-group structure, and surfaces:
 *   - new clusters (gaps to fill)
 *   - deprecated ad groups (market shifted away)
 *
 * Surface-only — no apply path. Operator who wants to act on a "new
 * cluster" finding clicks through to the Campaigns tab and creates it
 * via the P1 form. Operator who wants to deprecate an ad group does
 * so manually in Google Ads (or waits for the P2 weekly cron to flag
 * underperformers).
 */
import { useState } from 'react';
import {
  Loader2,
  Plus,
  RefreshCw,
  TriangleAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  analyzeClusterRefresh,
  type ClusterRefreshResponse,
  type DeprecatedAdGroup,
  type NewClusterCandidate,
} from '@/services/googleAdsMonthlyService';

interface ClusterRefreshTabProps {
  tenantId: string;
}

export function ClusterRefreshTab({ tenantId }: ClusterRefreshTabProps) {
  const [mode, setMode] = useState<'warn' | 'soft_auto'>('warn');
  const [lastResult, setLastResult] = useState<ClusterRefreshResponse | null>(null);
  const queryClient = useQueryClient();

  const analyzeMutation = useMutation({
    mutationFn: () => analyzeClusterRefresh({ tenantId, mode }),
    onSuccess: (result) => {
      setLastResult(result);
      const total =
        result.new_clusters.length + result.deprecated_ad_groups.length;
      if (result.skipped_reason) {
        toast.info(`Skipped: ${result.skipped_reason}`);
      } else if (total === 0) {
        toast.info(
          'Analysis complete — fresh research aligns with existing structure.',
        );
      } else {
        // CORE-K-04: when soft_auto reuses an existing pending approval
        // instead of queueing a new one, the toast names that so the
        // operator does not read a re-run as a fresh queue.
        const approvalSuffix = result.approval_id
          ? result.approval_reused
            ? ` (reused approval #${result.approval_id.slice(0, 8)})`
            : ` (approval #${result.approval_id.slice(0, 8)})`
          : '';
        toast.success(
          `${result.new_clusters.length} new + ` +
            `${result.deprecated_ad_groups.length} deprecated` +
            approvalSuffix,
        );
      }
      queryClient.invalidateQueries({
        queryKey: ['google-ads-weekly-approvals', tenantId],
      });
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Analysis failed: ${detail}`);
    },
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4 text-coral" />
            Refresh keyword universe
          </CardTitle>
          <CardDescription>
            Re-runs the keyword research pipeline (brainstorm → enrich →
            cluster → assign landing pages) against your current brand
            context, then diffs the result against your existing ENABLED
            ad groups. Surfaces clusters that have demand but no matching
            ad group ("gaps") and existing ad groups whose keywords
            don't appear in fresh research ("market shifted").
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                Tenant
              </label>
              <div className="rounded border border-border bg-warm-bg px-3 py-2 text-sm">
                {tenantId}
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-muted-foreground">
                Mode
              </label>
              <Select value={mode} onValueChange={(v) => setMode(v as typeof mode)}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="warn">warn (preview only)</SelectItem>
                  <SelectItem value="soft_auto">
                    soft_auto (queue approval)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={() => analyzeMutation.mutate()}
              disabled={analyzeMutation.isPending}
              className="bg-coral hover:bg-coral-hover"
            >
              {analyzeMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Re-running pipeline… (~60-90s)
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Run cluster refresh
                </>
              )}
            </Button>
          </div>
          <div className="text-xs text-muted-foreground">
            Surface-only — analysis flags opportunities, but creating new
            campaigns or pausing ad groups still happens via the
            Campaigns tab + Google Ads UI respectively.
          </div>
        </CardContent>
      </Card>

      {lastResult && <ResultPanel result={lastResult} />}
    </div>
  );
}

function ResultPanel({ result }: { result: ClusterRefreshResponse }) {
  const total =
    result.new_clusters.length + result.deprecated_ad_groups.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-teal" />
          Refresh result
          <Badge variant="outline" className="ml-2">
            {result.mode}
          </Badge>
          {result.approval_id && (
            // CORE-K-04: distinct label when the approval was reused
            // rather than freshly queued, so a second run does not
            // read as "queued again".
            <Badge
              variant="outline"
              className="border-coral/40 bg-coral-light text-coral-dark text-[10px]"
            >
              {result.approval_reused ? 'approval already open' : 'approval queued'}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Fresh: {result.fresh_cluster_count} cluster
          {result.fresh_cluster_count !== 1 ? 's' : ''} · Existing:{' '}
          {result.existing_ad_group_count} ad group
          {result.existing_ad_group_count !== 1 ? 's' : ''}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {result.skipped_reason && (
          <div className="flex items-center gap-2 rounded border border-coral/30 bg-coral-light/40 p-3 text-sm text-coral-dark">
            <TriangleAlert className="h-4 w-4" />
            Skipped: <code>{result.skipped_reason}</code>
          </div>
        )}

        {total === 0 && !result.skipped_reason && (
          <div className="rounded border border-teal/30 bg-teal-light/40 p-3 text-sm text-teal-dark">
            Fresh research aligns with your existing ad-group structure.
            No gaps + no deprecation signals this run.
          </div>
        )}

        {result.new_clusters.length > 0 && (
          <NewClustersSection items={result.new_clusters} />
        )}

        {result.deprecated_ad_groups.length > 0 && (
          <DeprecatedSection items={result.deprecated_ad_groups} />
        )}
      </CardContent>
    </Card>
  );
}

function NewClustersSection({ items }: { items: NewClusterCandidate[] }) {
  const navigate = useNavigate();
  return (
    <div>
      <div className="mb-2 flex items-center gap-2 text-sm font-medium">
        <Plus className="h-3.5 w-3.5 text-teal" />
        New clusters ({items.length})
      </div>
      <div className="space-y-2">
        {items.map((cluster, idx) => (
          <div
            key={`${cluster.cluster_name}-${idx}`}
            className="rounded border border-teal/30 bg-teal-light/30 p-3 text-sm"
          >
            <div className="mb-1 flex items-start justify-between">
              <span className="font-medium">{cluster.cluster_name}</span>
              <Button
                variant="outline"
                size="sm"
                className="h-7 border-teal/40 text-teal-dark hover:bg-teal-light"
                onClick={() => navigate('/ads-tools/campaigns?platform=google-ads')}
              >
                Open Campaign Creation
              </Button>
            </div>
            <div className="mb-1 flex flex-wrap gap-1">
              {cluster.keywords.map((kw) => (
                <Badge key={kw} variant="outline" className="bg-card text-xs">
                  {kw}
                </Badge>
              ))}
            </div>
            {cluster.landing_page && (
              <div className="text-xs text-muted-foreground">
                → <span className="font-mono">{cluster.landing_page}</span>
              </div>
            )}
            <div className="mt-1 text-[11px] text-muted-foreground">
              {cluster.suggested_action}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DeprecatedSection({ items }: { items: DeprecatedAdGroup[] }) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2 text-sm font-medium">
        <TriangleAlert className="h-3.5 w-3.5 text-coral" />
        Deprecated ad groups ({items.length})
      </div>
      <div className="space-y-2">
        {items.map((ag) => (
          <div
            key={ag.ad_group_id}
            className="rounded border border-coral/30 bg-coral-light/30 p-3 text-sm"
          >
            <div className="mb-1 flex items-start justify-between">
              <div>
                <span className="font-medium">{ag.ad_group_name}</span>
                <span className="ml-2 text-xs text-muted-foreground">
                  in {ag.campaign_name}
                </span>
              </div>
            </div>
            <div className="mb-1 flex flex-wrap gap-1">
              {ag.keywords.slice(0, 8).map((kw) => (
                <Badge
                  key={kw}
                  variant="outline"
                  className="bg-card text-xs text-muted-foreground line-through"
                >
                  {kw}
                </Badge>
              ))}
              {ag.keywords.length > 8 && (
                <Badge variant="outline" className="bg-card text-xs">
                  +{ag.keywords.length - 8} more
                </Badge>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {ag.suggested_action}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
